import {Box, ButtonBase, Paper, Skeleton, Typography} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {FC, useMemo} from "react";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LeaderboardModal} from "./LeaderboardModal";
import {LEADERBOARDS_CARD_BORDER} from "@/constants";
import {useLeaderboardCardModal} from "./useLeaderboardCardModal";
import {formatLeaderboardRank, getLeaderboardTrophyPublicPath} from "./leaderboardUiUtils";
import {useGetMe, useGetUserLeaderboard} from "@/app2/shared/data";

export type LeaderboardCardProps = {
	/**
	 * Cohort scope for `GET /api/_internal/leaderboards/:userId/user`.
	 * Defaults to `memberedCohort.id` from `/me` when present.
	 */
	cohortId?: number;
};

const useStyles = makeStyles()((theme) => ({
	outer: {
		width: "100%",
		height: "100%",
		display: "block",
		textAlign: "center",
		borderRadius: theme.spacing(3),
		[theme.breakpoints.up("sm")]: {
			textAlign: "left"
		},
		"&:hover > .MuiPaper-root, &:focus-visible > .MuiPaper-root": {
			border: `${theme.spacing(0.125)} solid ${theme.palette.primary.main}`
		}
	},
	paper: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		color: theme.palette.text.primary,
		padding: `${theme.spacing(3.5)} ${theme.spacing(3)}`,
		borderRadius: theme.spacing(3),
		backgroundColor: theme.palette.background.paperElevation1,
		border: `${theme.spacing(0.125)} solid ${LEADERBOARDS_CARD_BORDER}`,
		width: "100%",
		height: "100%",
		boxSizing: "border-box",
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(2),
			padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`
		},
		[theme.breakpoints.down("sm")]: {
			padding: `${theme.spacing(2.5)} ${theme.spacing(2)}`
		}
	},
	/** Rank + hint; left column on md+, stacked below trophy on small screens. */
	contentColumn: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		width: "100%",
		minWidth: 0,
		[theme.breakpoints.up("sm")]: {
			alignItems: "flex-start",
			flex: 1,
			width: "auto"
		}
	},
	trophyWrap: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		width: "157px",
		margin: "-30px",
		height: "159px",
		boxSizing: "border-box",
		[theme.breakpoints.up("sm")]: {
			justifyContent: "flex-start",
			alignSelf: "center"
		}
	},
	trophyImg: {
		width: "100%",
		height: "100%",
		display: "block",
		objectFit: "contain"
	},
	rankRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(0.5),
		[theme.breakpoints.up("sm")]: {
			marginTop: 0,
			marginBottom: theme.spacing(0.5),
			justifyContent: "flex-start"
		}
	},
	rankNumber: {
		fontFamily: theme.typography.fontFamily,
		fontSize: theme.typography.h4.fontSize,
		fontWeight: theme.typography.fontWeightBold,
		lineHeight: 1.2,
		color: theme.palette.primary.main,
		[theme.breakpoints.down("sm")]: {
			fontSize: theme.typography.h5.fontSize
		}
	},
	trendIcon: {
		fontSize: "34px",
		display: "flex",
		lineHeight: 1,
		marginLeft: theme.spacing(0.75)
	},
	trendUp: {
		color: theme.palette.success.main
	},
	trendDown: {
		color: theme.palette.error.main
	},
	pointsLine: {
		marginBottom: theme.spacing(1),
		textAlign: "center",
		[theme.breakpoints.up("sm")]: {
			textAlign: "left"
		}
	},
	pointsValue: {
		color: theme.palette.primary.main,
		fontWeight: theme.typography.fontWeightBold,
		fontVariantNumeric: "tabular-nums"
	},
	/** Subtitle layout; use with Typography variant="body2" color="text.secondary". */
	hint: {
		lineHeight: 1.45,
		textAlign: "center",
		maxWidth: theme.spacing(35),
		wordBreak: "break-word",
		overflowWrap: "anywhere",
		[theme.breakpoints.up("sm")]: {
			textAlign: "left",
			maxWidth: "100%",
			paddingRight: theme.spacing(1)
		}
	}
}));

const LeaderboardCard: FC<LeaderboardCardProps> = ({cohortId: cohortIdProp}) => {
	const {classes, cx} = useStyles();
	const {t} = useSSTranslation();
	const {open, openModal, closeModal} = useLeaderboardCardModal();

	const {data: me, isPending: isMePending} = useGetMe();
	const cohortParams = useMemo(() => {
		const id = cohortIdProp ?? me?.memberedCohort?.id;
		return id != null ? {cohortId: id} : undefined;
	}, [cohortIdProp, me?.memberedCohort?.id]);

	const {
		data: row,
		isPending: isLeaderboardPending,
		isFetching: isLeaderboardFetching,
		isError: isLeaderboardError
	} = useGetUserLeaderboard(me?.id, cohortParams, {enabled: !isMePending});

	const isLeaderboardLoading =
		isMePending ||
		(me?.id != null &&
			row === undefined &&
			!isLeaderboardError &&
			(isLeaderboardPending || isLeaderboardFetching));

	const monthRankingLabel = useMemo(() => {
		const anchor =
			row?.periodStartDate != null ? `${row.periodStartDate}T12:00:00.000Z` : undefined;
		const month = new Intl.DateTimeFormat(undefined, {
			month: "long",
			timeZone: anchor ? "UTC" : undefined
		}).format(anchor ? new Date(anchor) : new Date());
		return t("leaderboard_modal_month_ranking", "{{month}} ranking", {
			month: month.toUpperCase()
		});
	}, [t, row?.periodStartDate]);

	const pointsCol = t("leaderboard_modal_col_points", "Points");

	const rank = row?.rank ?? 0;
	const points = row?.totalPoints ?? 0;
	const isPositiveRank = row?.isPositiveRank ?? false;

	const trophySrc = useMemo(() => getLeaderboardTrophyPublicPath(rank), [rank]);

	const rankTrendGlyph = isPositiveRank ? (
		<ArrowDropUpIcon className={cx(classes.trendIcon, classes.trendUp)} aria-hidden />
	) : (
		<ArrowDropDownIcon className={cx(classes.trendIcon, classes.trendDown)} aria-hidden />
	);

	if (isLeaderboardLoading) {
		return (
			<Paper
				elevation={0}
				className={classes.paper}
				aria-busy
				aria-label={"Loading leaderboard"}
			>
				<Box className={classes.trophyWrap}>
					<Skeleton variant="rounded" width={157} height={159} sx={{borderRadius: 2}} />
				</Box>
				<Box className={classes.contentColumn}>
					<Box className={classes.rankRow}>
						<Skeleton variant="text" width={72} height={48} />
						<Skeleton variant="circular" width={34} height={34} sx={{ml: 1}} />
					</Box>
					<Skeleton variant="text" width="55%" height={28} sx={{mb: 1}} />
					<Skeleton variant="text" width="90%" height={22} />
				</Box>
			</Paper>
		);
	}

	if (isLeaderboardError || me?.id == null) {
		return (
			<Paper
				elevation={0}
				className={classes.paper}
				role="status"
				aria-live="polite"
			>
				<Box className={classes.contentColumn} sx={{alignItems: "center", width: "100%"}}>
					<Typography variant="body2" color="text.secondary" className={classes.hint}>
						{t(
							"leaderboard_card_load_error",
							"Leaderboard is unavailable right now. Try again later."
						)}
					</Typography>
				</Box>
			</Paper>
		);
	}

	return (
		<>
			<ButtonBase
				className={classes.outer}
				onClick={openModal}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={t("leaderboard_card_open_modal", "View leaderboard")}
			>
				<Paper elevation={0} className={classes.paper}>
					<Box className={classes.trophyWrap}>
						<Box
							component="img"
							src={trophySrc}
							alt=""
							className={classes.trophyImg}
							aria-hidden
						/>
					</Box>

					<Box className={classes.contentColumn}>
						<Box className={classes.rankRow}>
							<Typography variant="h4" component="div" className={classes.rankNumber}>
								#{formatLeaderboardRank(rank)}
							</Typography>
							{rankTrendGlyph}
						</Box>

						<Typography
							variant="body2"
							color="text.secondary"
							component="p"
							className={classes.pointsLine}
						>
							<Box component="span" className={classes.pointsValue}>
								{points.toLocaleString()}
							</Box>{" "}
							{pointsCol}
						</Typography>

						<Typography variant="body2" color="text.secondary" className={classes.hint}>
							{t(
								"leaderboard_card_position_hint",
								"Leaderboard Rank"
							)}
						</Typography>
					</Box>
				</Paper>
			</ButtonBase>

			<LeaderboardModal
				open={open}
				onClose={closeModal}
				monthRankingLabel={monthRankingLabel}
				titleRankNumber={rank}
				titleSuffix={t("leaderboard_modal_title_suffix", "on the board")}
				cohortId={row.cohortId}
				currentUserId={me.id}
			/>
		</>
	);
};

export default LeaderboardCard;
